import { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { nutritionAPI } from '../services/api'
import {
  UserIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ClockIcon,
  EnvelopeIcon,
  ClipboardDocumentIcon,
  PlusIcon,
  XMarkIcon,
  WrenchScrewdriverIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline'

// Profile Components
import HealthMetricsCard from '../components/profile/HealthMetricsCard'
import SecurityPanel from '../components/security/SecurityPanel'
import EquipmentSettingsCard from '../components/profile/EquipmentSettingsCard'
import MemoryCard from '../components/profile/MemoryCard'

const TABS = [
  { id: 'personal', icon: UserIcon, label: 'Personal' },
  { id: 'body', icon: ScaleIcon, label: 'Body' },
  { id: 'kitchen', icon: WrenchScrewdriverIcon, label: 'Kitchen' },
  // VH-235: what the app remembers about you.
  { id: 'memory', icon: LightBulbIcon, label: 'Memory' },
  { id: 'security', icon: ShieldCheckIcon, label: 'Security' }
]

export default function Profile() {
  const { t } = useTranslation()
  // VH-235: ?tab=memory opens a tab directly (the chat links to the memory list).
  const [activeTab, setActiveTab] = useState(() => {
    const wanted = new URLSearchParams(window.location.search).get('tab')
    return TABS.some((tab) => tab.id === wanted) ? wanted : 'personal'
  })
  const [editing, setEditing] = useState(false)
  const [editingMealTimes, setEditingMealTimes] = useState(false)
  const { user, updateProfile } = useAuth()

  // Personal info form
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      username: user?.username || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      height_cm: user?.height_cm || '',
      weight_kg: user?.weight_kg || '',
      date_of_birth: user?.date_of_birth ? user.date_of_birth.split('T')[0] : '',
      gender: user?.gender || '',
    }
  })

  // Meal time preferences state
  const [mealTimes, setMealTimes] = useState({
    breakfast_end: user?.meal_breakfast_end_hour || 11,
    lunch_end: user?.meal_lunch_end_hour || 14,
    snack_end: user?.meal_snack_end_hour || 17
  })
  const [savingMealTimes, setSavingMealTimes] = useState(false)

  // Email intake state
  const [intakeEmail, setIntakeEmail] = useState(null)
  const [intakeLoading, setIntakeLoading] = useState(false)
  const [intakeCopied, setIntakeCopied] = useState(false)
  const [allowedSenders, setAllowedSenders] = useState([])
  const [newSenderEmail, setNewSenderEmail] = useState('')
  const [savingSenders, setSavingSenders] = useState(false)
  const [sendersSaved, setSendersSaved] = useState(false)
  const [senderError, setSenderError] = useState(null)

  const fetchIntakeSettings = useCallback(async () => {
    setIntakeLoading(true)
    try {
      const response = await nutritionAPI.getEmailIntakeSettings()
      setIntakeEmail(response.data.email_address)
      setAllowedSenders(response.data.allowed_senders || [])
    } catch (err) {
      console.error('Failed to fetch intake settings:', err)
    } finally {
      setIntakeLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'personal') {
      fetchIntakeSettings()
    }
  }, [activeTab, fetchIntakeSettings])

  const handleCopyIntakeEmail = async () => {
    if (intakeEmail) {
      await navigator.clipboard.writeText(intakeEmail)
      setIntakeCopied(true)
      setTimeout(() => setIntakeCopied(false), 2000)
    }
  }

  const handleAddSender = () => {
    const email = newSenderEmail.trim().toLowerCase()
    if (email && !allowedSenders.includes(email)) {
      setAllowedSenders([...allowedSenders, email])
      setNewSenderEmail('')
      setSendersSaved(false)
    }
  }

  const handleRemoveSender = (email) => {
    setAllowedSenders(allowedSenders.filter(s => s !== email))
    setSendersSaved(false)
  }

  const handleSaveSenders = async () => {
    setSavingSenders(true)
    setSenderError(null)
    try {
      const response = await nutritionAPI.updateEmailIntakeAllowedSenders(allowedSenders)
      setAllowedSenders(response.data.allowed_senders || [])
      setSendersSaved(true)
      setTimeout(() => setSendersSaved(false), 2000)
    } catch (err) {
      if (err.response?.status === 409) {
        setSenderError(err.response.data?.detail || t('profile.emailIntake.senderConflict', 'This email is already registered by another user.'))
      } else {
        console.error('Failed to save allowed senders:', err)
      }
    } finally {
      setSavingSenders(false)
    }
  }

  const onSubmit = async (data) => {
    const result = await updateProfile(data)
    if (result.success) {
      setEditing(false)
    }
  }

  const handleSaveMealTimes = async () => {
    setSavingMealTimes(true)
    try {
      const result = await updateProfile({
        meal_breakfast_end_hour: mealTimes.breakfast_end,
        meal_lunch_end_hour: mealTimes.lunch_end,
        meal_snack_end_hour: mealTimes.snack_end
      })
      if (result.success) {
        setEditingMealTimes(false)
      }
    } finally {
      setSavingMealTimes(false)
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <div className="space-y-6">
            {/* Personal Information Card */}
            <div className="card">
              <div className="card-header flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('profile.personalInformation', 'Personal Information')}
                </h3>
                <button
                  onClick={() => setEditing(!editing)}
                  className={editing ? "btn-secondary" : "btn-primary"}
                >
                  {editing ? t('common.cancel', 'Cancel') : t('common.edit', 'Edit')}
                </button>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="form-label">{t('profile.username', 'Username')}</label>
                      <input
                        {...register('username', { required: t('profile.usernameRequired', 'Username is required') })}
                        type="text"
                        disabled={!editing}
                        className="form-input"
                      />
                      {errors.username && <p className="form-error">{errors.username.message}</p>}
                    </div>
                    <div>
                      <label className="form-label">{t('profile.email', 'Email')}</label>
                      {/* VH-285: the email is the login name; it is shown, never changed here. */}
                      <input
                        value={user?.email || ''}
                        type="email"
                        readOnly
                        disabled
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('profile.firstName', 'First Name')}</label>
                      <input
                        {...register('first_name')}
                        type="text"
                        disabled={!editing}
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('profile.lastName', 'Last Name')}</label>
                      <input
                        {...register('last_name')}
                        type="text"
                        disabled={!editing}
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('profile.height', 'Height')} (cm)</label>
                      <input
                        {...register('height_cm')}
                        type="number"
                        disabled={!editing}
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('profile.weight', 'Weight')} (kg)</label>
                      <input
                        {...register('weight_kg')}
                        type="number"
                        disabled={!editing}
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('profile.dateOfBirth', 'Date of Birth')}</label>
                      <input
                        {...register('date_of_birth')}
                        type="date"
                        disabled={!editing}
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">{t('profile.gender', 'Gender')}</label>
                      <select
                        {...register('gender')}
                        disabled={!editing}
                        className="form-input"
                      >
                        <option value="">{t('profile.selectGender', 'Select Gender')}</option>
                        <option value="male">{t('profile.genders.male', 'Male')}</option>
                        <option value="female">{t('profile.genders.female', 'Female')}</option>
                        <option value="other">{t('profile.genders.other', 'Other')}</option>
                      </select>
                    </div>
                  </div>

                  {editing && (
                    <div className="flex justify-end">
                      <button type="submit" className="btn-primary">
                        {t('common.saveChanges', 'Save Changes')}
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Meal Time Preferences Card */}
            <div className="card">
              <div className="card-header flex justify-between items-center">
                <div className="flex items-center">
                  <ClockIcon className="h-6 w-6 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('profile.mealTimePreferences', 'Meal Time Preferences')}
                  </h3>
                </div>
                <button
                  onClick={() => setEditingMealTimes(!editingMealTimes)}
                  className={editingMealTimes ? "btn-secondary" : "btn-primary"}
                >
                  {editingMealTimes ? t('common.cancel', 'Cancel') : t('common.edit', 'Edit')}
                </button>
              </div>
              <div className="card-body">
                <p className="text-sm text-gray-600 mb-4">
                  {t('profile.mealTimeDescription', 'Configure when each meal type ends. Used for automatic meal categorization from photo timestamps.')}
                </p>

                <div className="space-y-4">
                  {/* Breakfast ends at */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="form-label mb-0">🌅 {t('nutrition.mealTypes.breakfast', 'Breakfast')}</label>
                      <p className="text-xs text-gray-500">
                        {t('profile.mealTimeRange', '00:00 - {{hour}}:00', { hour: mealTimes.breakfast_end })}
                      </p>
                    </div>
                    {editingMealTimes ? (
                      <select
                        value={mealTimes.breakfast_end}
                        onChange={(e) => setMealTimes({ ...mealTimes, breakfast_end: parseInt(e.target.value) })}
                        className="form-select w-24"
                      >
                        {[...Array(24)].map((_, i) => (
                          <option key={i} value={i}>{i}:00</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-gray-900 font-medium">{t('profile.endsAt', 'Ends at')} {mealTimes.breakfast_end}:00</span>
                    )}
                  </div>

                  {/* Lunch ends at */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="form-label mb-0">🌞 {t('nutrition.mealTypes.lunch', 'Lunch')}</label>
                      <p className="text-xs text-gray-500">
                        {t('profile.mealTimeRange', '{{start}}:00 - {{end}}:00', { start: mealTimes.breakfast_end, end: mealTimes.lunch_end })}
                      </p>
                    </div>
                    {editingMealTimes ? (
                      <select
                        value={mealTimes.lunch_end}
                        onChange={(e) => setMealTimes({ ...mealTimes, lunch_end: parseInt(e.target.value) })}
                        className="form-select w-24"
                      >
                        {[...Array(24)].map((_, i) => (
                          <option key={i} value={i}>{i}:00</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-gray-900 font-medium">{t('profile.endsAt', 'Ends at')} {mealTimes.lunch_end}:00</span>
                    )}
                  </div>

                  {/* Snack ends at */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="form-label mb-0">🍎 {t('nutrition.mealTypes.snack', 'Snack')}</label>
                      <p className="text-xs text-gray-500">
                        {t('profile.mealTimeRange', '{{start}}:00 - {{end}}:00', { start: mealTimes.lunch_end, end: mealTimes.snack_end })}
                      </p>
                    </div>
                    {editingMealTimes ? (
                      <select
                        value={mealTimes.snack_end}
                        onChange={(e) => setMealTimes({ ...mealTimes, snack_end: parseInt(e.target.value) })}
                        className="form-select w-24"
                      >
                        {[...Array(24)].map((_, i) => (
                          <option key={i} value={i}>{i}:00</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-gray-900 font-medium">{t('profile.endsAt', 'Ends at')} {mealTimes.snack_end}:00</span>
                    )}
                  </div>

                  {/* Dinner (always last) */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="form-label mb-0">🌙 {t('nutrition.mealTypes.dinner', 'Dinner')}</label>
                      <p className="text-xs text-gray-500">
                        {t('profile.mealTimeRange', '{{start}}:00 - 24:00', { start: mealTimes.snack_end })}
                      </p>
                    </div>
                    <span className="text-gray-500 text-sm">{t('profile.untilMidnight', 'Until midnight')}</span>
                  </div>
                </div>

                {editingMealTimes && (
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={handleSaveMealTimes}
                      disabled={savingMealTimes}
                      className="btn-primary"
                    >
                      {savingMealTimes ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Email Food Intake Card */}
            <div className="card">
              <div className="card-header flex justify-between items-center">
                <div className="flex items-center">
                  <EnvelopeIcon className="h-6 w-6 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('profile.emailIntake.title', 'Email Food Intake')}
                  </h3>
                </div>
              </div>
              <div className="card-body">
                <p className="text-sm text-gray-600 mb-4">
                  {t('profile.emailIntake.description', 'Send a food photo to your personal email address below to automatically log a meal.')}
                </p>

                {intakeLoading && !intakeEmail ? (
                  <div className="text-sm text-gray-500">{t('common.loading', 'Loading...')}</div>
                ) : intakeEmail ? (
                  <div className="space-y-4">
                    <div>
                      <label className="form-label mb-1">{t('profile.emailIntake.yourAddress', 'Your intake email')}</label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm font-mono break-all">
                          {intakeEmail}
                        </code>
                        <button
                          onClick={handleCopyIntakeEmail}
                          className="btn-secondary flex items-center gap-1 whitespace-nowrap"
                          title={t('profile.emailIntake.copy', 'Copy to clipboard')}
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                          {intakeCopied
                            ? t('profile.emailIntake.copied', 'Copied!')
                            : t('profile.emailIntake.copy', 'Copy')}
                        </button>
                      </div>
                    </div>

                    {/* Allowed Senders Section */}
                    <div className="pt-4 border-t border-gray-100">
                      <label className="form-label mb-1">{t('profile.emailIntake.allowedSenders', 'Allowed sender emails')}</label>
                      <p className="text-xs text-gray-500 mb-2">
                        {t('profile.emailIntake.allowedSendersDescription', 'Only emails from these addresses will be accepted. Add at least one sender.')}
                      </p>

                      {allowedSenders.length === 0 && (
                        <p className="text-xs text-amber-600 mb-2">
                          {t('profile.emailIntake.noSendersWarning', 'No sender emails configured. Incoming photos will be rejected.')}
                        </p>
                      )}

                      {allowedSenders.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {allowedSenders.map((email) => (
                            <div key={email} className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-md">
                              <span className="flex-1 text-sm font-mono">{email}</span>
                              <button
                                onClick={() => handleRemoveSender(email)}
                                className="text-gray-400 hover:text-red-500"
                                title={t('profile.emailIntake.removeSender', 'Remove')}
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <input
                          type="email"
                          value={newSenderEmail}
                          onChange={(e) => setNewSenderEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSender())}
                          placeholder={t('profile.emailIntake.senderPlaceholder', 'email@example.com')}
                          className="form-input flex-1 text-sm"
                        />
                        <button
                          onClick={handleAddSender}
                          disabled={!newSenderEmail.trim()}
                          className="btn-secondary flex items-center gap-1 text-sm"
                        >
                          <PlusIcon className="h-4 w-4" />
                          {t('profile.emailIntake.addSender', 'Add')}
                        </button>
                      </div>

                      {senderError && (
                        <p className="text-xs text-red-600 mt-1">{senderError}</p>
                      )}

                      <div className="flex justify-end mt-3">
                        <button
                          onClick={handleSaveSenders}
                          disabled={savingSenders}
                          className="btn-primary text-sm"
                        >
                          {savingSenders
                            ? t('profile.emailIntake.savingSenders', 'Saving...')
                            : sendersSaved
                              ? t('profile.emailIntake.sendersSaved', 'Senders saved!')
                              : t('profile.emailIntake.saveSenders', 'Save senders')}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

          </div>
        )

      case 'body':
        return (
          <div className="space-y-6">
            <HealthMetricsCard />
          </div>
        )

      case 'kitchen':
        return (
          <div className="space-y-6">
            <EquipmentSettingsCard />
          </div>
        )

      case 'memory':
        return <MemoryCard />
      case 'security':
        // VH-285: the same as the Security page (/security).
        return <SecurityPanel />

      default:
        return null
    }
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('profile.title', 'Profile')}</h1>
        <p className="mt-2 text-gray-600">
          {t('profile.description', 'Manage your personal information, health goals, and account settings')}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${isActive
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className={`h-5 w-5 mr-2 ${isActive ? 'text-indigo-500' : 'text-gray-400'}`} />
                {t(`profile.tabs.${tab.id}`, tab.label)}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl">
        {renderTabContent()}
      </div>
    </div>
  )
}
